import React, { useState } from "react";
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Stack,
  Tooltip,
  IconButton,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import QuizIcon from "@mui/icons-material/Quiz";
import { useGetPublicMockTestHistoryQuery } from "@/store/api/user/publicMockTestApi";
import { partLabel } from "./partMeta";
import PublicMockTestResultDetailDialog from "./ResultDetailDialog";

// Match the Public Mock Test page palette: soft lavender (violet 500 → 400 → 300).
const BRAND_GRADIENT =
  "linear-gradient(135deg, #8b5cf6 0%, #a78bfa 50%, #c4b5fd 100%)";
const BRAND_PRIMARY = "#7c3aed";
const BRAND_TINT_BG = "rgba(139,92,246,0.08)";
const BRAND_TINT_STRONG = "rgba(139,92,246,0.12)";
const BRAND_TINT_BORDER = "rgba(139,92,246,0.4)";

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

/**
 * Reusable panel rendering the logged-in user's public mock test history with
 * the summary / full / part toggle. Used inside the unified Statistics menu
 * (and previously the standalone history page). It owns nothing but the local
 * `type` toggle — the fetch is delegated to `useGetPublicMockTestHistoryQuery`.
 */
const PublicMockTestHistoryPanel = () => {
  const [type, setType] = useState("summary");
  const [selectedAttempt, setSelectedAttempt] = useState(null);

  const { data, isFetching, isError, error } =
    useGetPublicMockTestHistoryQuery(type);

  const rows = Array.isArray(data) ? data : [];

  return (
    <Paper
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: 4,
        background: "white",
        boxShadow: "0 12px 32px rgba(124,58,237,0.10)",
        border: "1px solid rgba(139,92,246,0.12)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mb: 2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              p: 1.25,
              borderRadius: 2,
              background: BRAND_GRADIENT,
              display: "inline-flex",
              color: "white",
              boxShadow: "0 6px 16px rgba(124,58,237,0.28)",
            }}
          >
            <QuizIcon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a" }}>
              Public Mock Test History
            </Typography>
            <Typography variant="caption" sx={{ color: "#64748b" }}>
              Your guest-mode attempts, grouped by mode
            </Typography>
          </Box>
        </Box>

        <ToggleButtonGroup
          value={type}
          exclusive
          onChange={(_, value) => value && setType(value)}
          size="small"
          sx={{
            "& .MuiToggleButton-root": {
              textTransform: "none",
              fontWeight: 600,
              px: 2,
              borderColor: BRAND_TINT_BORDER,
              color: BRAND_PRIMARY,
              "&.Mui-selected": {
                background: BRAND_GRADIENT,
                color: "white",
                borderColor: "transparent",
                "&:hover": { background: BRAND_GRADIENT, opacity: 0.95 },
              },
            },
          }}
        >
          <ToggleButton value="summary">Summary</ToggleButton>
          <ToggleButton value="full">Full</ToggleButton>
          <ToggleButton value="part">Part</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {isFetching && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress sx={{ color: BRAND_PRIMARY }} />
        </Box>
      )}

      {isError && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error?.data?.message || "Failed to load history."}
        </Alert>
      )}

      {!isFetching && !isError && rows.length === 0 && (
        <Alert
          severity="info"
          sx={{
            borderRadius: 2,
            bgcolor: BRAND_TINT_BG,
            border: "1px solid rgba(139,92,246,0.2)",
            "& .MuiAlert-icon": { color: BRAND_PRIMARY },
          }}
        >
          You have no public mock test attempts yet.
        </Alert>
      )}

      {!isFetching && !isError && rows.length > 0 && (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            borderRadius: 3,
            borderColor: "rgba(139,92,246,0.18)",
            overflow: "hidden",
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    background:
                      "linear-gradient(135deg, rgba(139,92,246,0.10) 0%, rgba(167,139,250,0.10) 100%)",
                    fontWeight: 700,
                    color: "#0f172a",
                    borderBottom: "2px solid rgba(139,92,246,0.25)",
                  },
                }}
              >
                <TableCell>Test</TableCell>
                <TableCell>Mode</TableCell>
                {type === "full" && (
                  <>
                    <TableCell align="right">Score</TableCell>
                    <TableCell align="right">Listening</TableCell>
                    <TableCell align="right">Reading</TableCell>
                  </>
                )}
                {type === "summary" && (
                  <TableCell align="right">Score</TableCell>
                )}
                {type === "part" && <TableCell>Parts</TableCell>}
                <TableCell align="right">Completed</TableCell>
                <TableCell align="right">Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.attemptId}
                  hover
                  sx={{
                    cursor: "pointer",
                    transition: "background 0.15s ease",
                    "&:hover": {
                      backgroundColor: "rgba(139,92,246,0.06) !important",
                    },
                  }}
                  onClick={() => setSelectedAttempt(row)}
                >
                  <TableCell sx={{ fontWeight: 600, color: "#0f172a" }}>
                    {row.examTitle}
                  </TableCell>
                  <TableCell sx={{ textTransform: "capitalize" }}>
                    <Chip
                      size="small"
                      label={row.mode}
                      sx={{
                        bgcolor: BRAND_TINT_STRONG,
                        color: BRAND_PRIMARY,
                        fontWeight: 600,
                        borderRadius: 1.5,
                      }}
                    />
                  </TableCell>
                  {type === "full" && (
                    <>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {row.score ?? "—"}
                      </TableCell>
                      <TableCell align="right">
                        {row.listeningScore ??
                          row.breakdown?.listening?.score ??
                          "—"}
                      </TableCell>
                      <TableCell align="right">
                        {row.readingScore ??
                          row.breakdown?.reading?.score ??
                          "—"}
                      </TableCell>
                    </>
                  )}
                  {type === "summary" && (
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {row.mode === "part" ? "—" : row.score ?? "—"}
                    </TableCell>
                  )}
                  {type === "part" && (
                    <TableCell>
                      <Stack
                        direction="row"
                        spacing={0.5}
                        flexWrap="wrap"
                        useFlexGap
                      >
                        {(row.partScores || []).map((p) => (
                          <Chip
                            key={p.partNumber}
                            size="small"
                            variant="outlined"
                            label={`${partLabel(p.partNumber)}: ${p.correct}/${p.total}`}
                            sx={{
                              borderColor: BRAND_TINT_BORDER,
                              color: BRAND_PRIMARY,
                              fontWeight: 500,
                            }}
                          />
                        ))}
                      </Stack>
                    </TableCell>
                  )}
                  <TableCell align="right" sx={{ color: "#64748b" }}>
                    {formatDate(row.completedAt)}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="View answer details">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedAttempt(row);
                        }}
                        sx={{
                          color: BRAND_PRIMARY,
                          "&:hover": {
                            background: BRAND_TINT_STRONG,
                          },
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <PublicMockTestResultDetailDialog
        open={Boolean(selectedAttempt)}
        attempt={selectedAttempt}
        onClose={() => setSelectedAttempt(null)}
      />
    </Paper>
  );
};

export default PublicMockTestHistoryPanel;
